// PurchaseOrderGeneralInfo.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';

// Utilities
import { coerceToDateTimeRangeValue } from 'utilities/DateTime';
import { getOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';
import { buildNodeFieldPatch } from 'utilities/NodeFieldData';
import {
  formatPurchaseOrderEnum,
  getPurchaseOrderGeneralInfoModel,
  readPurchaseOrderField,
} from 'nodetypes/purchaseorder/view/purchaseOrderGeneralInfoModel';
import { getPurchaseOrderSchemaField } from 'nodetypes/purchaseorder/view/purchaseOrderPartyUtils';

// CSS
import './PurchaseOrderGeneralInfo.css';

// Components
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';
import NodeDateInput from 'node/form/fields/date/NodeDateInput';
import DocumentLetterhead from 'nodetypes/documents/kit/DocumentLetterhead';
import PurchaseOrderShipToParty from 'nodetypes/purchaseorder/view/PurchaseOrderShipToParty';
import PurchaseOrderVendorParty from 'nodetypes/purchaseorder/view/PurchaseOrderVendorParty';

const PO_DATE_FIELDS = Object.freeze([
  { name: 'billedDate', label: 'Billed Date', modelKey: 'billedDate' },
  { name: 'sendDate', label: 'Date', modelKey: 'sendDate' },
  { name: 'dueDate', label: 'Due Date', modelKey: 'dueDate' },
]);

const PO_DATE_FRIENDLY_FORMAT = Object.freeze({
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

function DisplayValue({ value, emptyLabel = '—' }) {
  const cleaned = String(value || '').trim();
  return (
    <div className={`po-general-info-value${cleaned ? '' : ' is-empty'}`}>
      {cleaned || emptyLabel}
    </div>
  );
}

/**
 * PurchaseOrderGeneralInfo
 *
 * Houzz-inspired General Info block for the PO viewer (read-oriented v1).
 */
const PurchaseOrderGeneralInfo = React.memo(({
  node,
  nodeType = null,
  readOnly = false,
  onNodeChange,
  letterheadOverride = null,
  relatedNodesByKey = null,
}) => {
  // ----- CONTEXT ----- //

  const language = useCurrentLanguage();

  const canEdit = Boolean(!readOnly && typeof onNodeChange === 'function' && node?.key);
  const typeKey = String(nodeType?.key || node?.type || 'PURCHASE_ORDER').trim().toUpperCase();
  const nodeTitle = String(node?.title || '');
  const nodeNumber = useMemo(() => {
    const raw = readPurchaseOrderField(node, 'number', nodeType);
    return raw == null ? '' : String(raw).trim();
  }, [node, nodeType]);

  // ----- STATE ----- //

  const [titleDraft, setTitleDraft] = useState(nodeTitle);
  const [numberDraft, setNumberDraft] = useState(nodeNumber);

  // ----- EFFECTS ----- //

  useEffect(() => {
    setTitleDraft(nodeTitle);
  }, [node?.key, nodeTitle]);

  useEffect(() => {
    setNumberDraft(nodeNumber);
  }, [node?.key, nodeNumber]);

  // ----- MEMOS ----- //

  const model = useMemo(
    () => getPurchaseOrderGeneralInfoModel(node, nodeType),
    [node, nodeType],
  );

  const paymentTermsField = useMemo(
    () => getPurchaseOrderSchemaField(nodeType, 'paymentTerms'),
    [nodeType],
  );

  const paymentTermsValue = useMemo(() => {
    const raw = readPurchaseOrderField(node, 'paymentTerms', nodeType);
    if (raw == null || raw === '') return '';
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return String(raw.value || raw.key || '').trim();
    }
    return String(raw).trim();
  }, [node, nodeType]);

  const paymentTermsOptions = useMemo(() => {
    const options = resolveEnumOptionsForField(paymentTermsField, language)
      .map((option) => {
        const value = String(option?.value ?? '').trim();
        if (!value) return null;
        return {
          value,
          label: getOptionLabel(option, language) || value,
          iconKey: String(option?.iconKey || '').trim(),
          color: typeof option?.color === 'string' ? option.color : '',
          note: String(option?.note || '').trim(),
        };
      })
      .filter(Boolean);

    const allowClear = paymentTermsField?.allowClear !== false;
    if (!allowClear) return options;
    return [{ value: '', label: '—' }, ...options];
  }, [language, paymentTermsField]);

  const paymentTermsDisplay = useMemo(() => {
    if (!paymentTermsValue) return '';
    const match = paymentTermsOptions.find((option) => option.value === paymentTermsValue);
    if (match?.label) return match.label;
    return formatPurchaseOrderEnum(readPurchaseOrderField(node, 'paymentTerms', nodeType));
  }, [node, nodeType, paymentTermsOptions, paymentTermsValue]);

  const dateFieldValues = useMemo(() => {
    const next = {};
    PO_DATE_FIELDS.forEach(({ name }) => {
      next[name] = coerceToDateTimeRangeValue(
        readPurchaseOrderField(node, name, nodeType),
      );
    });
    return next;
  }, [node, nodeType]);

  // ----- HANDLERS ----- //

  const handleTitleChange = useCallback((event) => {
    if (!canEdit) return;
    setTitleDraft(event?.target?.value ?? '');
  }, [canEdit]);

  const handleTitleBlur = useCallback(() => {
    if (!canEdit) return;
    const nextTitle = String(titleDraft || '').trim();
    const currentTitle = String(node?.title || '').trim();
    if (nextTitle === currentTitle) {
      setTitleDraft(node?.title || '');
      return;
    }
    onNodeChange(node.key, { title: nextTitle });
  }, [canEdit, node, onNodeChange, titleDraft]);

  const handlePaymentTermsChange = useCallback((event) => {
    if (!canEdit) return;
    const nextValue = String(event?.target?.value ?? event ?? '').trim();
    const field = paymentTermsField || {
      name: 'paymentTerms',
      source: typeKey.toLowerCase(),
      type: 'enum',
    };
    const patch = buildNodeFieldPatch(node, field, typeKey, nextValue);
    if (!patch) return;
    onNodeChange(node.key, patch);
  }, [canEdit, node, onNodeChange, paymentTermsField, typeKey]);

  const handleNumberChange = useCallback((event) => {
    if (!canEdit) return;
    setNumberDraft(event?.target?.value ?? '');
  }, [canEdit]);

  const handleNumberBlur = useCallback(() => {
    if (!canEdit) return;
    const nextNumber = String(numberDraft || '').trim();
    if (nextNumber === nodeNumber) {
      setNumberDraft(nodeNumber);
      return;
    }
    const schemaField = getPurchaseOrderSchemaField(nodeType, 'number');
    const field = schemaField || {
      name: 'number',
      source: typeKey.toLowerCase(),
      type: 'string',
    };
    const patch = buildNodeFieldPatch(node, field, typeKey, nextNumber);
    if (!patch) return;
    onNodeChange(node.key, patch);
  }, [canEdit, node, nodeNumber, nodeType, numberDraft, onNodeChange, typeKey]);

  const handleDateFieldChange = useCallback((fieldName) => (nextValue) => {
    if (!canEdit) return;
    const schemaField = getPurchaseOrderSchemaField(nodeType, fieldName);
    const field = schemaField || {
      name: fieldName,
      source: typeKey.toLowerCase(),
      type: 'datetime',
    };
    const patch = buildNodeFieldPatch(node, field, typeKey, nextValue);
    if (!patch) return;
    onNodeChange(node.key, patch);
  }, [canEdit, node, nodeType, onNodeChange, typeKey]);

  // ----- RENDER ----- //

  return (
    <div className="po-general-info">
      <div className="po-general-info-parties">
        <div className="po-general-info-party">
          <DocumentLetterhead letterheadOverride={letterheadOverride} />
        </div>

        <PurchaseOrderVendorParty
          node={node}
          nodeType={nodeType}
          readOnly={readOnly}
          onNodeChange={onNodeChange}
          relatedNodesByKey={relatedNodesByKey}
        />

        <PurchaseOrderShipToParty
          node={node}
          nodeType={nodeType}
          readOnly={readOnly}
          onNodeChange={onNodeChange}
          relatedNodesByKey={relatedNodesByKey}
        />
      </div>

      <div className="po-general-info-meta">
        <div className="po-general-info-title-block">
          {canEdit ? (
            <div className="po-general-info-title-input">
              <TextInput
                value={titleDraft}
                placeholder="Add a title…"
                hybrid
                autoFocus={false}
                autoSelect={false}
                width="100%"
                style={{
                  fontSize: '22px',
                  fontWeight: 700,
                  lineHeight: 1.3,
                  paddingLeft: 10,
                  paddingRight: 8,
                  paddingTop: 2,
                  paddingBottom: 2,
                }}
                onChange={handleTitleChange}
                onBlur={handleTitleBlur}
              />
            </div>
          ) : (
            <div className="po-general-info-title">{model.title}</div>
          )}
        </div>

        <div className="po-general-info-fields">
          <div className="po-general-info-field">
            <div className="po-general-info-label">Purchase Order #</div>
            {canEdit ? (
              <div className="po-general-info-number-input">
                <TextInput
                  value={numberDraft}
                  placeholder="PO number…"
                  hybrid
                  autoFocus={false}
                  autoSelect={false}
                  width="100%"
                  style={{
                    fontSize: '14px',
                    fontWeight: 700,
                    paddingLeft: 6,
                    paddingRight: 6,
                    paddingTop: 2,
                    paddingBottom: 2,
                  }}
                  onChange={handleNumberChange}
                  onBlur={handleNumberBlur}
                />
              </div>
            ) : (
              <DisplayValue value={model.number} emptyLabel="—" />
            )}
          </div>

          <div className="po-general-info-field">
            <div className="po-general-info-label">Payment Terms</div>
            {canEdit ? (
              <div className="po-general-info-field-select">
                <SelectInput
                  value={paymentTermsValue}
                  options={paymentTermsOptions}
                  placeholder="—"
                  showIcons={false}
                  width="100%"
                  hybrid
                  style={{
                    fontWeight: 700,
                    height: '32px',
                  }}
                  onChange={handlePaymentTermsChange}
                />
              </div>
            ) : (
              <DisplayValue value={paymentTermsDisplay} emptyLabel="—" />
            )}
          </div>

          {PO_DATE_FIELDS.map(({ name, label, modelKey }) => (
            <div className="po-general-info-field" key={name}>
              <div className="po-general-info-label">{label}</div>
              {canEdit ? (
                <div className="po-general-info-field-date">
                  <NodeDateInput
                    value={dateFieldValues[name]}
                    hybrid
                    iconSize={18}
                    friendlyFormatOptions={PO_DATE_FRIENDLY_FORMAT}
                    friendlyFontWeight={700}
                    onChange={handleDateFieldChange(name)}
                  />
                </div>
              ) : (
                <DisplayValue value={model[modelKey]} emptyLabel="—" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

PurchaseOrderGeneralInfo.displayName = 'PurchaseOrderGeneralInfo';

export default PurchaseOrderGeneralInfo;
